import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, fileUrl } from "@/api";
import { useI18n } from "@/contexts/I18nContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SPECIALIZATIONS } from "@/lib/specializations";
import LanguageToggle from "@/components/LanguageToggle";
import NmcBadge from "@/components/NmcBadge";
import { Switch } from "@/components/ui/switch";
import { Search, MapPin, Stethoscope, Activity, ArrowRight, Building2, ShieldCheck } from "lucide-react";

export default function PatientHome() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [spec, setSpec] = useState("");
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [doctors, setDoctors] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [geo, setGeo] = useState(null);
  const [isGovScheme, setIsGovScheme] = useState(false);
  const [selectedScheme, setSelectedScheme] = useState("rghs");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q) params.set("q", q);
      if (city) params.set("city", city);
      if (spec) params.set("specialization", spec);
      if (verifiedOnly) params.set("verified_only", "true");
      let endpoint = `${API}/public/search`;
      if (isGovScheme) {
        endpoint = `${API}/public/search/government-schemes`;
        params.set("scheme", selectedScheme);
      }
      const { data } = await axios.get(`${endpoint}?${params.toString()}`);
      setDoctors(data.doctors || []);
      setOrgs(data.orgs || []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [verifiedOnly, isGovScheme, selectedScheme]);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [verifiedOnly]);

  // Batch-load feedback summaries for the visible doctor list
  const [ratings, setRatings] = useState({});
  useEffect(() => {
    if (!doctors.length) return;
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(doctors.map(async (d) => {
        try {
          const { data } = await axios.get(`${API}/public/doctors/${d.id}/feedback-summary`);
          return [d.id, data];
        } catch { return [d.id, null]; }
      }));
      if (cancelled) return;
      setRatings(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [doctors]);

  const useLocation = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setGeo({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => setGeo({ error: true })
    );
  };

  return (
    <div className="min-h-screen bg-slate-50 bg-grain" data-testid="patient-home-page">
      <header className="px-4 sm:px-8 py-4 flex items-center justify-between bg-white border-b">
        <Link to="/" className="flex items-center gap-2" data-testid="patient-home-logo">
          <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-sky-500 to-emerald-500 flex items-center justify-center">
            <Activity className="w-5 h-5 text-white" />
          </div>
          <span className="font-heading text-base font-bold tracking-tight text-slate-900">
            {t("app_name")}
          </span>
        </Link>
        <LanguageToggle />
      </header>

      <section className="px-4 sm:px-8 py-8 sm:py-12 max-w-6xl mx-auto">
        <p className="text-xs tracking-[0.3em] uppercase text-sky-600 font-semibold">{t("find_doctor")}</p>
        <h1 className="font-heading text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 leading-[1.1] mt-3">
          {t("patient_app_title")}
        </h1>
        <p className="mt-4 text-base text-slate-600 max-w-2xl">{t("patient_app_subtitle")}</p>

        <Card className="medical-card p-4 sm:p-6 mt-8" data-testid="patient-search-card">
          <div className="flex gap-2 mb-4">
            <Button
              variant={!isGovScheme ? "default" : "outline"}
              onClick={() => setIsGovScheme(false)}
              className="flex-1"
            >
              Regular Search
            </Button>
            <Button
              variant={isGovScheme ? "default" : "outline"}
              onClick={() => setIsGovScheme(true)}
              className="flex-1 bg-emerald-500 hover:bg-emerald-600"
            >
              🏥 RGHS / Ayushman
            </Button>
          {isGovScheme && (
            <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <label className="text-sm font-semibold text-emerald-900 block mb-2">Select Scheme:</label>
              <div className="flex gap-2">
                <Button
                  variant={selectedScheme === "rghs" ? "default" : "outline"}
                  onClick={() => setSelectedScheme("rghs")}
                  className="flex-1 text-sm"
                >
                  RGHS (Rajasthan)
                </Button>
                <Button
                  variant={selectedScheme === "ayushman" ? "default" : "outline"}
                  onClick={() => setSelectedScheme("ayushman")}
                  className="flex-1 text-sm"
                >
                  Ayushman Bharat
                </Button>
              </div>
            </div>
          )}
          </div>
          <div className="grid md:grid-cols-12 gap-3">
            <div className="md:col-span-5 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search_placeholder")} className="pl-9" data-testid="patient-search-q" />
            </div>
            <div className="md:col-span-3 relative">
              <MapPin className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder={t("city_placeholder")} className="pl-9" data-testid="patient-search-city" />
            </div>
            <div className="md:col-span-3">
              <Select value={spec} onValueChange={(v) => setSpec(v === "__all__" ? "" : v)}>
                <SelectTrigger data-testid="patient-search-spec"><SelectValue placeholder={t("all_specializations")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">{t("all_specializations")}</SelectItem>
                  {SPECIALIZATIONS.map((s) => (
                    <SelectItem key={s.en} value={s.en}>
                      {lang === "hi" ? `${s.hi} (${s.en})` : `${s.en} · ${s.hi}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-1">
              <Button onClick={load} className="w-full h-10 bg-sky-500 hover:bg-sky-600 text-white" data-testid="patient-search-submit">
                <Search className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-slate-500">
            <button onClick={useLocation} className="inline-flex items-center gap-1 text-sky-600 hover:underline" data-testid="patient-use-location">
              <MapPin className="w-3.5 h-3.5" /> {t("use_my_location")}
            </button>
            {geo && !geo.error && <span data-testid="geo-coords">📍 {geo.lat.toFixed(3)}, {geo.lng.toFixed(3)}</span>}
            <label className="ml-auto inline-flex items-center gap-2 select-none" data-testid="verified-only-toggle-wrap">
              <span className="flex items-center gap-1 text-slate-600">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                Verified only
              </span>
              <Switch
                checked={verifiedOnly}
                onCheckedChange={setVerifiedOnly}
                data-testid="verified-only-toggle"
              />
            </label>
          </div>
        </Card>
      </section>

      <section className="px-4 sm:px-8 pb-20 max-w-6xl mx-auto">
        {loading ? (
          <p className="text-slate-500">{t("loading")}</p>
        ) : doctors.length === 0 ? (
          <Card className="medical-card p-10 text-center" data-testid="patient-no-results">
            <p className="text-slate-500">{t("no_results")}</p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {doctors.map((d) => (
              <Card
                key={d.id}
                className="medical-card p-5 cursor-pointer hover:-translate-y-0.5 transition-transform"
                onClick={() => navigate(`/patient/doctor/${d.id}`)}
                data-testid={`patient-doctor-card-${d.id}`}
              >
                <div className="flex items-start gap-4">
                  <div className="w-14 h-14 rounded-xl overflow-hidden bg-gradient-to-br from-sky-100 to-emerald-100 flex items-center justify-center shrink-0">
                    {d.profile_pic ? <img src={fileUrl(d.profile_pic)} alt="" className="w-full h-full object-cover" /> :
                      <Stethoscope className="w-7 h-7 text-sky-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-heading text-base font-semibold text-slate-900 truncate">{d.name}</h3>
                      <NmcBadge doctor={d} testid={`nmc-badge-${d.id}`} />
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {lang === "hi" && d.specialization_hi ? d.specialization_hi : d.specialization_en}
                    </p>
                    <div className="mt-2 flex items-center gap-2 text-xs text-slate-500">
                      <Building2 className="w-3 h-3" /> <span className="truncate">{d.org?.name}</span>
                    </div>
                    {d.trust_score > 0 && (
                      <div className="mt-1.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[10px] font-semibold border border-emerald-200" data-testid={`trust-score-${d.id}`}>
                        <ShieldCheck className="w-3 h-3" /> {d.trust_score} {d.trust_score === 1 ? "referral" : "referrals"}
                      </div>
                    )}
                    {ratings[d.id]?.review_count > 0 && (
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-amber-700" data-testid={`rating-summary-${d.id}`}>
                        <span className="inline-flex">
                          {[1,2,3,4,5].map((i) => (
                            <span key={i} className={`text-sm leading-none ${i <= Math.round(ratings[d.id].avg_rating || 0) ? "text-amber-500" : "text-slate-300"}`}>★</span>
                          ))}
                        </span>
                        <span>{ratings[d.id].avg_rating?.toFixed(1)}</span>
                        <span className="text-slate-400 font-normal">({ratings[d.id].review_count} review{ratings[d.id].review_count === 1 ? "" : "s"})</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <Badge variant="secondary" className="bg-emerald-50 text-emerald-700 hover:bg-emerald-50">
                    ₹{d.consultation_fee} · {d.avg_diagnosis_time} {t("minutes")}
                  </Badge>
                  <ArrowRight className="w-4 h-4 text-slate-400" />
                </div>
              </Card>
            ))}
          </div>
        )}

        {orgs.length > 0 && (
          <div className="mt-10">
            <h2 className="font-heading text-lg font-semibold text-slate-900 mb-3">Listed providers</h2>
            <div className="flex flex-wrap gap-2">
              {orgs.map((o) => (
                <Badge key={o.id} variant="outline" className="text-xs">
                  {o.name}{o.city ? ` · ${o.city}` : ""}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
